import React from 'react';
import Icon from 'components/AppIcon';

const UserStatusIndicator = ({
  username = 'Commander',
  level = 1,
  energy = 75,
  maxEnergy = 100,
  xp = 4200,
  xpToNext = 5000,
  jailTimer = 0,
  avatarUrl = '',
  onProfileClick,
  onJailClick,
  isCollapsed = false,
}) => {
  const energyPercent = Math.min(100, Math.max(0, (energy / maxEnergy) * 100));
  const xpPercent = Math.min(100, Math.max(0, (xp / xpToNext) * 100));
  const isJailed = jailTimer > 0;

  const formatJailTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s?.toString()?.padStart(2, '0')}`;
  };

  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center gap-2 p-3 border-t border-border">
        <button
          onClick={onProfileClick}
          className="w-9 h-9 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center hover:bg-primary/20 transition-all duration-250 glow-cyan-sm"
          title={username}
        >
          <Icon name="User" size={16} color="var(--color-primary)" />
        </button>
        {isJailed && (
          <button
            onClick={onJailClick}
            className="w-9 h-9 rounded-md bg-error/10 border border-error/30 flex items-center justify-center hover:bg-error/20 transition-all duration-250"
            title={`Jailed: ${formatJailTime(jailTimer)}`}
          >
            <Icon name="Lock" size={14} color="var(--color-error)" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 border-t border-border space-y-3">
      {/* Jail Warning */}
      {isJailed && (
        <button
          onClick={onJailClick}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-error/10 border border-error/30 hover:bg-error/15 transition-all duration-250 cursor-pointer"
        >
          <Icon name="Lock" size={14} color="var(--color-error)" />
          <span className="font-caption text-xs text-error flex-1 text-left">JAILED</span>
          <span className="font-caption text-xs text-error font-medium">{formatJailTime(jailTimer)}</span>
        </button>
      )}
      {/* User Info */}
      <button
        onClick={onProfileClick}
        className="w-full flex items-center gap-3 hover:bg-primary/5 rounded-md p-1 transition-all duration-250 cursor-pointer"
      >
        <div className="w-9 h-9 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0 glow-cyan-sm">
          <Icon name="User" size={16} color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <span className="font-heading text-sm font-medium text-foreground truncate">{username}</span>
            <span className="font-caption text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded">LVL {level}</span>
          </div>
          <span className="font-caption text-xs text-muted-foreground">View Profile</span>
        </div>
        <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
      </button>
      {/* Energy Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Icon name="Zap" size={11} color="var(--color-accent)" />
            <span className="font-caption text-xs text-muted-foreground">Energy</span>
          </div>
          <span className="font-caption text-xs text-accent">{energy}/{maxEnergy}</span>
        </div>
        <div className="user-status-bar">
          <div
            className="user-status-bar-fill"
            style={{
              width: `${energyPercent}%`,
              background: energyPercent > 50
                ? 'var(--color-accent)'
                : energyPercent > 20
                ? 'var(--color-warning)'
                : 'var(--color-error)',
              boxShadow: energyPercent > 50 ? 'var(--glow-amber)' : 'none',
            }}
          />
        </div>
      </div>
      {/* XP Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Icon name="Star" size={11} color="var(--color-primary)" />
            <span className="font-caption text-xs text-muted-foreground">XP</span>
          </div>
          <span className="font-caption text-xs text-primary">{xp?.toLocaleString()}/{xpToNext?.toLocaleString()}</span>
        </div>
        <div className="user-status-bar">
          <div
            className="user-status-bar-fill"
            style={{
              width: `${xpPercent}%`,
              background: 'var(--color-primary)',
              boxShadow: 'var(--glow-sm)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default UserStatusIndicator;